import React, { useCallback, ReactNode, useMemo } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Heading from "@atlaskit/heading";
import { RovoLogo } from "@atlaskit/logo";
import { Box, Stack, Inline, Text, xcss } from "@atlaskit/primitives";

import { Adg4Button } from "@/components/adg4/button/src";
import { Adg4TextUnderline } from "@/components/adg4/text-underline/src";
import { SignupBackgroundLayout } from "@/components/signup/layouts";
import { Link } from "@/libraries/design/components";
import { isProduction } from "@/libraries/signup/bxpkit-core";

import { useAnalyticsClient } from "../../../../../../../../src/analytics/wac-analytics/analytics-client";
import { useLocalizedHref } from "../../../../../../../libraries/web-framework/src/localization/use-localized-href";
import { messages } from "../messages";

import { ScribbleImage } from "./scribble-svg";

const elevatedContainerInnerStyles = xcss({
    textAlign: "center",
    paddingBlockStart: "space.500"
});
const bodyTextStyles = xcss({ paddingBlockStart: "space.100" });
const loginButtonStyles = xcss({
    paddingBlockStart: "space.400",
    paddingBlockEnd: "space.400",
    paddingInlineStart: "space.800",
    paddingInlineEnd: "space.800"
});
const contactAtlassianStyles = xcss({
    justifyContent: "center"
});
const relative = css({
    position: "relative",
    zIndex: 0
});
const whiteBackground = css({
    position: "absolute",
    insetBlockStart: 0,
    insetBlockEnd: 0,
    insetInlineStart: 0,
    insetInlineEnd: 0,
    width: "100%",
    height: "100%",
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "#ffffff",
    zIndex: -1
});

export const LoginContactSales = () => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();

    const localizedContactHref = useLocalizedHref("/enterprise/contact");

    const fireButtonAnalytics = useCallback(
        (buttonName: string) => {
            analyticsClient &&
                createAnalyticsEvent({
                    action: "clicked",
                    actionSubject: buttonName,
                    actionSubjectId: "softwareRovoStartPageTemplate",
                    attributes: {
                        source: "loginContactSalesScreen"
                    }
                }).fire();
        },
        [analyticsClient, createAnalyticsEvent]
    );

    const loginButtonHandler = useCallback(
        () => fireButtonAnalytics("loginButton"),
        [fireButtonAnalytics]
    );

    const contactSalesLinkHandler = useCallback(
        () => fireButtonAnalytics("contactSalesLink"),
        [fireButtonAnalytics]
    );

    const loginUrl = useMemo(() => {
        if (typeof window === "undefined") {
            return "id.atlassian.com/";
        }
        return isProduction()
            ? "https://id.atlassian.com/"
            : "https://id.stg.internal.atlassian.com/";
    }, []);

    const LinktoContactAtlassian = (props: { children: ReactNode }) => (
        <Link
            href={localizedContactHref}
            type="primary"
            ariaLabel="Contact Atlassian Page"
            aria-label={"Contact Atlassian Page"}
            text={props.children as string}
            onClick={contactSalesLinkHandler}
        />
    );

    const allItemsContactAtlassian = intl.formatMessage(
        messages.contactAtlassianText,
        {
            a: (contactAtlassian: React.ReactNode) => (
                <LinktoContactAtlassian>
                    {contactAtlassian}
                </LinktoContactAtlassian>
            )
        }
    );

    return (
        <SignupBackgroundLayout
            ProductLogo={<RovoLogo size="xlarge" appearance="brand" />}
        >
            <Stack xcss={elevatedContainerInnerStyles}>
                <div css={relative}>
                    <div css={whiteBackground} />
                    <Heading as="h1">
                        <FormattedMessage
                            {...messages.rovoGetStarted}
                            values={{
                                started: (str: ReactNode) => (
                                    <Adg4TextUnderline
                                        underline={<ScribbleImage />}
                                        fontWeight={{
                                            from: 600,
                                            to: 600
                                        }}
                                    >
                                        {str}
                                    </Adg4TextUnderline>
                                )
                            }}
                        />
                    </Heading>
                </div>
                <Box xcss={bodyTextStyles}>
                    <Text>
                        <FormattedMessage {...messages.rovoBodyText} />
                    </Text>
                </Box>
                <Box xcss={loginButtonStyles}>
                    <Adg4Button
                        size="small"
                        isFullWidth={true}
                        aria-label="Login Button"
                        href={`${loginUrl}login?continue=${encodeURIComponent(
                            window.location.href
                        )}`}
                        onClick={loginButtonHandler}
                        target=""
                    >
                        {intl.formatMessage(messages.loginButtonRovo)}
                    </Adg4Button>
                </Box>
                <Inline space="space.100" xcss={contactAtlassianStyles}>
                    <FormattedMessage {...messages.dontHaveAccountText} />
                    <Box>{allItemsContactAtlassian}</Box>
                </Inline>
            </Stack>
        </SignupBackgroundLayout>
    );
};
